import React, { useEffect, useMemo, useRef } from "react";
import { Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useIntegrationOAuth } from "@/hooks/useIntegrationOAuth";
import { getServiceForTool, toolRequiresOAuth, type ServiceConfig } from "@/config/integrations";
import { useIntegrationStatusStore } from "@/stores/IntegrationStatusStore";
import { logger } from "@/telemetry";

interface IntegrationWarningBannerProps {
  /** List of tool IDs that are currently enabled in this chat/workflow */
  enabledToolIds: string[];
  /** Optional callback when banner is dismissed */
  onDismiss?: () => void;
}

/**
 * Warning banner that alerts users when enabled tools have expired tokens.
 * Shows at the top of chat sessions and workflow pages.
 *
 * @example
 * <IntegrationWarningBanner
 *   enabledToolIds={["google-drive-read", "jira-get-issue"]}
 *   onDismiss={() => setDismissed(true)}
 * />
 */
export const IntegrationWarningBanner: React.FC<IntegrationWarningBannerProps> = ({
  enabledToolIds,
  onDismiss,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { prefetchStatuses } = useIntegrationOAuth();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hadExpiredRef = useRef(false);

  // Subscribe to store statuses reactively — re-renders when prefetchStatuses updates the store
  const statuses = useIntegrationStatusStore((state) => state.statuses);

  // Derive expired services reactively from store + enabledToolIds
  const expiredServices = useMemo(() => {
    const expired: ServiceConfig[] = [];

    enabledToolIds.forEach((toolId) => {
      if (!toolRequiresOAuth(toolId)) return;
      const service = getServiceForTool(toolId);

      if (!service) return;
      const status = statuses[service.id];

      if (
        status?.connected &&
        (status.token_status === "expired" || status.token_status === "refresh_failed")
      ) {
        if (!expired.find((e) => e.id === service.id)) {
          expired.push(service);
        }
      }
    });

    return expired;
  }, [enabledToolIds, statuses]);

  // Auto-dismiss when all integrations are reconnected after being expired
  useEffect(() => {
    if (expiredServices.length > 0) {
      hadExpiredRef.current = true;
    } else if (hadExpiredRef.current && onDismiss) {
      logger.debug("🔄 IntegrationWarningBanner: All integrations reconnected, auto-dismissing");
      onDismiss();
    }
  }, [expiredServices, onDismiss]);

  // Fetch statuses on mount
  useEffect(() => {
    prefetchStatuses();
  }, [prefetchStatuses]);

  // Poll integration status every 10 seconds when there are expired services
  useEffect(() => {
    if (expiredServices.length > 0) {
      logger.debug("🔄 IntegrationWarningBanner: Starting polling for", {
        services: expiredServices.map((s) => s.name).join(", "),
      });

      pollingIntervalRef.current = setInterval(() => {
        logger.debug("🔄 IntegrationWarningBanner: Polling integration status...");
        prefetchStatuses(true); // Force bypass cache TTL
      }, 10000);
    }

    return () => {
      if (pollingIntervalRef.current) {
        logger.debug("🔄 IntegrationWarningBanner: Stopping polling");
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [expiredServices, prefetchStatuses]);

  if (expiredServices.length === 0) {
    return null;
  }

  const serviceNames = expiredServices.map((s) => s.name).join(", ");

  return (
    <div
      className="mx-4 mt-4 p-3 bg-warning-50 dark:bg-warning-50/10 border border-warning-200 rounded-lg"
      data-testid="integration-warning-banner"
    >
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 text-warning shrink-0 mt-0.5" icon="solar:danger-triangle-bold" />

        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-warning">
            {t("Some integrations need reconnection")}
          </div>
          <div className="text-xs text-warning-600 dark:text-warning-400 mt-1">
            {expiredServices.length === 1
              ? t("{{service}} token has expired", { service: serviceNames })
              : t("The following services have expired tokens: {{services}}", {
                  services: serviceNames,
                })}
          </div>
          <div className="text-xs text-warning-600 dark:text-warning-400 mt-0.5">
            {t("Some tools may not work until you reconnect.")}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            className="text-xs h-7"
            color="warning"
            data-testid="go-to-settings-btn"
            size="sm"
            startContent={<Icon icon="solar:settings-bold" />}
            variant="flat"
            onPress={() => navigate("/settings#integrations")}
          >
            {t("Go to Settings")}
          </Button>
          {onDismiss && (
            <Button
              isIconOnly
              className="text-xs h-7"
              color="default"
              data-testid="dismiss-warning-btn"
              size="sm"
              variant="light"
              onPress={onDismiss}
            >
              <Icon icon="solar:close-circle-bold" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
